import React from 'react';
import { NodeProps, useReactFlow } from 'reactflow';
import axios from 'axios';
import Cookies from 'universal-cookie';
import { Select } from '../../../../form/select';
import { Textarea } from '../../../../form/textarea';
import { TextInput } from '../../../../form/textInput';
import { operationType, selectDataType } from '../../../../../services/types';
import { webURL } from '../../../../../services/API';
import { Main, Styled } from '../browserUse/style';
import { SettingsBlock, settingsDataType } from '../../../../settingsBlock';
import { IoCheckmarkCircleOutline, IoWarningOutline } from 'react-icons/io5';
import { ExpandedBlock } from '../../../expanded';

type ComputerUseProps = NodeProps<operationType> & {
   setChildMinimize?: (fn: (id: string) => void) => void;
   setChildSettings?: (fn: (id: string) => void) => void;
};

const DEFAULT_VALUE = {
   request: 'agent_decide',
   task: '',
   startUrl: '',
   application: 'firefox',
   fileUrls: '',
   desktopProvider: 'e2b',
   allowedHosts: '',
   maxSteps: 20,
   timeoutMs: 600000,
   controllerModel: 'gemini-2.5-flash',
};

const OPERATION_OPTIONS: selectDataType[] = [
   { id: 1, name: 'Let AI Agent Decide', refId: 'agent_decide' },
   { id: 2, name: 'Run desktop task', refId: 'run_task' },
];

const DESKTOP_OPTIONS: selectDataType[] = [
   { id: 1, name: 'Isolated Linux sandbox (E2B)', refId: 'e2b' },
   { id: 2, name: 'My PC (Dragify Desktop)', refId: 'agent' },
   { id: 3, name: 'This Windows PC (local dev)', refId: 'local' },
];

const MODEL_OPTIONS: selectDataType[] = [
   { id: 1, name: 'Gemini 2.5 Flash', refId: 'gemini-2.5-flash' },
   { id: 2, name: 'Gemini 2.5 Pro', refId: 'gemini-2.5-pro' },
   { id: 3, name: 'Local vision model (this PC)', refId: 'local' },
];

function clampMaxSteps(value: any) {
   const parsed = Number.parseInt(String(value ?? ''), 10);
   if (!Number.isFinite(parsed)) return 20;
   return Math.min(40, Math.max(1, parsed));
}

function clampTimeoutMs(value: any) {
   const parsed = Number.parseInt(String(value ?? ''), 10);
   if (!Number.isFinite(parsed)) return 600000;
   return Math.min(3600000, Math.max(30000, parsed));
}

type DesktopAgentStatus = {
   connected: boolean;
   controlEnabled: boolean;
   hostname?: string;
   ready?: boolean;
};

function authHeaders() {
   const token = new Cookies().get('token');
   return { headers: { 'x-auth-token': token } };
}

function DesktopAgentStatusPanel() {
   const [status, setStatus] = React.useState<DesktopAgentStatus | null>(null);
   const [error, setError] = React.useState('');

   const refresh = React.useCallback(async () => {
      try {
         const { data } = await axios.get(`${webURL}/desktop-agent/status`, authHeaders());
         setStatus(data);
         setError('');
      } catch (err: any) {
         setStatus({ connected: false, controlEnabled: false });
         setError(err?.response?.data?.error || err?.message || 'Could not reach Dragify Desktop.');
      }
   }, []);

   React.useEffect(() => {
      refresh();
      const timer = window.setInterval(refresh, 3000);
      return () => window.clearInterval(timer);
   }, [refresh]);

   const connected = Boolean(status?.connected);

   return (
      <div className="desktopControl">
         <div className="desktopControlHeader">
            <div className="desktopControlTitle">This PC</div>
         </div>
         <div className="desktopControlStatus">
            {connected
               ? `Connected${status?.hostname ? ` to ${status.hostname}` : ''}. Computer Use will control this Windows PC automatically.`
               : 'Dragify Desktop is offline. Open the desktop app and sign in — then this node connects automatically.'}
            {error ? ` ${error}` : ''}
         </div>
      </div>
   );
}

export function ComputerUse({ id, data, setChildMinimize, setChildSettings }: ComputerUseProps) {
   const { setNodes } = useReactFlow();
   const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
   const [isExpanded, setIsExpanded] = React.useState(false);
   const [operations, setOperations] = React.useState<selectDataType[]>(
      OPERATION_OPTIONS.map((item) => ({ ...item, isActive: item.refId === 'agent_decide' }))
   );
   const [models, setModels] = React.useState<selectDataType[]>(
      MODEL_OPTIONS.map((item) => ({ ...item, isActive: item.refId === 'gemini-2.5-flash' }))
   );
   const [desktops, setDesktops] = React.useState<selectDataType[]>(
      DESKTOP_OPTIONS.map((item) => ({ ...item, isActive: item.refId === 'e2b' }))
   );

   const patchValue = React.useCallback((patch: Record<string, any>) => {
      setNodes((nds) => nds.map((node) => {
         if (node.id !== id) return node;
         return { ...node, data: { ...node.data, value: { ...DEFAULT_VALUE, ...node.data.value, ...patch } } };
      }));
   }, [id, setNodes]);

   React.useEffect(() => {
      if (setChildMinimize) setChildMinimize(() => { setIsExpanded((prev) => !prev); });
      if (setChildSettings) setChildSettings(() => { setSettings((prev) => ({ ...prev, isToggle: !prev.isToggle })); });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [setChildMinimize, setChildSettings]);

   React.useEffect(() => {
      if (!data.value) {
         patchValue(DEFAULT_VALUE);
         return;
      }
      const nextRequest = data.value.request || 'agent_decide';
      const nextModel = data.value.controllerModel || 'gemini-2.5-flash';
      const nextDesktop = data.value.desktopProvider || 'e2b';
      setOperations((items) => items.map((item) => ({ ...item, isActive: item.refId === nextRequest })));
      setModels((items) => items.map((item) => ({ ...item, isActive: item.refId === nextModel })));
      setDesktops((items) => items.map((item) => ({ ...item, isActive: item.refId === nextDesktop })));
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   const handleSelectOperation = (selectId: number) => {
      const selected = OPERATION_OPTIONS.find((item) => item.id === selectId);
      setOperations((items) => items.map((item) => ({ ...item, isActive: item.id === selectId })));
      patchValue({ request: selected?.refId || 'agent_decide' });
   };

   const handleSelectModel = (selectId: number) => {
      const selected = MODEL_OPTIONS.find((item) => item.id === selectId);
      setModels((items) => items.map((item) => ({ ...item, isActive: item.id === selectId })));
      patchValue({ controllerModel: selected?.refId || 'gemini-2.5-flash' });
   };

   const handleSelectDesktop = (selectId: number) => {
      const selected = DESKTOP_OPTIONS.find((item) => item.id === selectId);
      setDesktops((items) => items.map((item) => ({ ...item, isActive: item.id === selectId })));
      patchValue({ desktopProvider: selected?.refId || 'e2b' });
   };

   const desktopProvider = data.value?.desktopProvider || 'e2b';
   const isAgentPc = desktopProvider === 'agent';
   const isLocalPc = desktopProvider === 'local';
   const isWarning = isAgentPc || isLocalPc;

   return (<>
      <SettingsBlock nodeId={id} data={settings} setData={setSettings} />
      <Main>
         {isWarning ? <>
            <div id="warning"></div>
            <span id="warningIcon"><IoWarningOutline size={21} /></span>
         </> : data.output ? <>
            <div id="ran"></div>
            <span id="ranIcon"><IoCheckmarkCircleOutline size={21} /></span>
         </> : null}

         {isExpanded ? <ExpandedBlock
            data={{ id, ...data }}
            handleClose={() => setIsExpanded(false)}
            isWarning={isWarning}
            style={{ minWidth: '420px' }}
         ><Styled>
               <Select title="Operation" data={operations} handleSelectItem={handleSelectOperation} isSmall />
               <Select title="Desktop connection" data={desktops} handleSelectItem={handleSelectDesktop} isSmall />
               {isAgentPc ? <DesktopAgentStatusPanel /> : null}
               <Textarea
                  title="Task"
                  value={data.value?.task || ''}
                  handleChange={(e) => patchValue({ task: e.target.value })}
               />
               <TextInput
                  title="Start URL"
                  value={data.value?.startUrl || ''}
                  placeholder="https://example.com"
                  handleChange={(e) => patchValue({ startUrl: e.target.value })}
               />
               <TextInput
                  title="Application"
                  value={data.value?.application || 'firefox'}
                  placeholder="firefox, xfce4-terminal, libreoffice-writer, libreoffice-calc"
                  handleChange={(e) => patchValue({ application: e.target.value })}
               />
               <Textarea
                  title="File URLs to import (one per line)"
                  value={data.value?.fileUrls || ''}
                  handleChange={(e) => patchValue({ fileUrls: e.target.value })}
               />
               <Textarea
                  title="Allowed hosts"
                  value={data.value?.allowedHosts || ''}
                  handleChange={(e) => patchValue({ allowedHosts: e.target.value })}
               />
               <TextInput
                  type="number"
                  title="Max steps"
                  value={String(data.value?.maxSteps ?? 20)}
                  placeholder="20"
                  min={1}
                  max={40}
                  isNormal={false}
                  handleChange={(e) => patchValue({ maxSteps: e.target.value === '' ? '' : clampMaxSteps(e.target.value) })}
               />
               <TextInput
                  type="number"
                  title="Timeout (ms)"
                  value={String(data.value?.timeoutMs ?? 600000)}
                  placeholder="600000"
                  min={30000}
                  max={3600000}
                  isNormal={false}
                  handleChange={(e) => patchValue({ timeoutMs: e.target.value === '' ? '' : clampTimeoutMs(e.target.value) })}
               />
               <Select title="Controller model" data={models} handleSelectItem={handleSelectModel} isSmall />
               <div className="guidance">
                  {isAgentPc ? <>
                     <p>Controls your Windows PC through Dragify Desktop. Keep the desktop app open and signed in — this node connects automatically.</p>
                     <p>The agent can open Edge/Chrome, PowerShell, Notepad, Explorer, Word, and Excel on this machine. Files go to your Documents folder (~/documents).</p>
                     <p>Do not use this on a shared PC. The agent can click anything visible on your desktop, including the Dragify window.</p>
                  </> : isLocalPc ? <>
                     <p>This controls the Windows PC that is running User-Backend — real mouse, keyboard, and screen. Set COMPUTER_USE_LOCAL_ENABLED=true in the backend .env and restart.</p>
                     <p>The agent can open Edge/Chrome, PowerShell, Notepad, Explorer, Word, and Excel on this machine. Files go to your Documents folder (~/documents).</p>
                     <p>Do not use this on a shared or production server. The agent can click anything visible on your desktop.</p>
                  </> : <>
                     <p>This starts a paid isolated Linux desktop. Connect it to the AI Agent Tools handle.</p>
                     <p>Use Browser Use for ordinary website extraction. Use Computer Use when the agent must see and operate a full desktop, including apps like Firefox or LibreOffice.</p>
                     <p>Login, 2FA, payments, sending, deleting, and uploads pause for you. Do not put passwords in this block.</p>
                  </>}
               </div>
            </Styled></ExpandedBlock> : null}
      </Main>
   </>);
}
